import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import actions from '@store/actions/listingSearchPage';
import {
  useListingSearchCriteria,
  ListingSearchCriteria,
  priceTitle,
  sizeTitle,
  leaseTypeTitle,
} from '@components/layouts/Truva/ListingSearchDeprecated/utils';
import { Button } from '@components/shared';
import SimpleDropdown from '@components/shared/Dropdown/SimpleDropdown';
import useToggle from '@shared/useToggle';
import SizeFilter from './SizeFilter';
import s from './Filters.module.less';
import PriceFilter from './PriceFilter';
import FirstToMarketFilter from './FirstToMarketFilter';
import LeaseTypeFilter from './LeaseTypeFilter';
import SaveSearch from './SaveSearch';
import AllFiltersOverlay from './AllFiltersOverlay';

const Filters = () => {
  const { t } = useTranslation('filters');
  const history = useHistory();
  const criteria = useListingSearchCriteria();
  const [mounted, setMounted] = useState(false);

  const {
    value: allFilterOverlayIsVisible,
    toggle: toggleAllFilterOverlayVisibility,
    setFalse: closeAllFilterOverlayVisibility,
  } = useToggle();

  const dispatch = useDispatch();
  const openFilter = (filterName: string) => dispatch(actions.openFilter(filterName));
  const trackOpenAllFilters = () => dispatch(actions.allFiltersOpened());
  const trackCloseAllFilters = () => dispatch(actions.allFiltersClosed());
  const trackClearAllFilters = () => dispatch(actions.allFiltersCleared());

  const onChange = (passedCriteria: ListingSearchCriteria) => {
    history.push(passedCriteria.toUrl());
  };

  const debouncedOnChange = debounce((callbackFn: () => ListingSearchCriteria) => {
    const newCriteria = callbackFn();
    onChange(newCriteria);
  }, 500);

  useEffect(() => {
    if (mounted) {
      if (allFilterOverlayIsVisible) {
        trackOpenAllFilters();
      } else {
        trackCloseAllFilters();
      }
    } else {
      setMounted(true);
    }
    // return () => {};
  }, [allFilterOverlayIsVisible]);

  return (
    <>
      <div className={s.quickFilterRow}>
        <div className={s.filtersContainer}>
          <FirstToMarketFilter criteria={criteria} onChange={onChange} />
          <SimpleDropdown title={priceTitle(t, criteria)} onOpen={() => openFilter('price')}>
            <PriceFilter onChange={debouncedOnChange} criteria={criteria} type="quick" />
          </SimpleDropdown>
          <div className={s.secondaryQuickFilter}>
            <SimpleDropdown title={sizeTitle(t, criteria)} onOpen={() => openFilter('size')}>
              <SizeFilter type="quick" onChange={debouncedOnChange} criteria={criteria} />
            </SimpleDropdown>
          </div>
          <div className={s.secondaryQuickFilter}>
            <SimpleDropdown
              title={leaseTypeTitle(t, criteria)}
              onOpen={() => openFilter('leaseTypes')}
            >
              <LeaseTypeFilter type="quick" onChange={onChange} criteria={criteria} />
            </SimpleDropdown>
          </div>
          <Button type="tertiary" size="medium" onClick={toggleAllFilterOverlayVisibility}>
            {criteria.numFilters() > 0
              ? t('allFiltersWithCount', { count: criteria.numFilters() })
              : t('allFilters')}
          </Button>
        </div>
        <div className={s.spacer} />
        <div className={s.savedSearch}>
          <SaveSearch criteria={criteria} />
        </div>
      </div>
      {allFilterOverlayIsVisible ? (
        <AllFiltersOverlay
          onClose={closeAllFilterOverlayVisibility}
          onClear={trackClearAllFilters}
        />
      ) : null}
    </>
  );
};

export default Filters;
